/**
 * Single source of truth for THIS tab's active account pin.
 *
 * Replaces the positional `/u/<n>` path prefix. Key invariants:
 *   1. The active account for a tab lives in `sessionStorage` under
 *      {@link TAB_ACCOUNT_STORAGE_KEY} as an opaque `userId` (NOT an index).
 *   2. Reading is synchronous so `getAuthToken`, `useSession`, and PowerSync's
 *      database-binding resolve immediately on boot/navigation without
 *      flashing guest state.
 *   3. Writing the pin also records {@link LAST_ACTIVE_ACCOUNT_KEY} in
 *      `localStorage` so a fresh tab (which starts with empty `sessionStorage`)
 *      inherits the device's last-activated account instead of falling back to
 *      index 0.
 *   4. Clean URL handoff: opening a link from this tab in a new tab carries
 *      `?as=<alias>` (a device-local one-shot opaque token minted by
 *      `account-alias.ts`). {@link readTabAccountId} consumes and immediately
 *      strips that parameter from the URL via `history.replaceState` before the
 *      router renders, ensuring URLs in the address bar stay canonical.
 */

import { createSignal } from "solid-js";
import { accountIdForAlias } from "./account-alias";

export const TAB_ACCOUNT_STORAGE_KEY = "malla_tab_account";
export const LAST_ACTIVE_ACCOUNT_KEY = "malla_last_active_account";
export const TAB_ACCOUNT_CHANGED_EVENT = "malla:tab-account-changed";
export const ACCOUNTS_CHANGED_EVENT = "malla:accounts-changed";

export const ACCOUNT_HANDOFF_PARAM = "as";

/** Signal holding the current tab's active account ID (reactive bridge). */
const [tabAccountSignal, setTabAccountSignal] = createSignal<string | null>(
  readTabAccountIdInitial(),
);

/**
 * Latch ensuring `?as=<alias>` is consumed at most ONCE per document lifetime,
 * even if multiple sub-modules read the active account before the URL parameter
 * is stripped.
 */
let handoffConsumed = false;

function parseHandoffParam(): string | null {
  if (handoffConsumed) {
    return null;
  }
  if (globalThis.location === undefined || !globalThis.location.search) {
    return null;
  }
  try {
    const params = new URLSearchParams(globalThis.location.search);
    const alias = params.get(ACCOUNT_HANDOFF_PARAM);
    if (!alias) {
      return null;
    }
    handoffConsumed = true;
    params.delete(ACCOUNT_HANDOFF_PARAM);
    const query = params.toString();
    const cleanUrl =
      globalThis.location.pathname + (query ? `?${query}` : "") + globalThis.location.hash;
    if (globalThis.history !== undefined && globalThis.history.replaceState) {
      globalThis.history.replaceState(globalThis.history.state, "", cleanUrl);
    }
    return accountIdForAlias(alias);
  } catch {
    return null;
  }
}

function readTabAccountIdInitial(): string | null {
  if (globalThis.sessionStorage === undefined) {
    return null;
  }
  try {
    const fromHandoff = parseHandoffParam();
    if (fromHandoff) {
      globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, fromHandoff);
      if (globalThis.localStorage !== undefined) {
        globalThis.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, fromHandoff);
      }
      return fromHandoff;
    }
    return globalThis.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Synchronously reads this tab's pinned account ID. */
export function readTabAccountId(): string | null {
  if (globalThis.sessionStorage === undefined) {
    return null;
  }
  try {
    const fromHandoff = parseHandoffParam();
    if (fromHandoff) {
      globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, fromHandoff);
      if (globalThis.localStorage !== undefined) {
        globalThis.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, fromHandoff);
      }
      setTabAccountSignal(fromHandoff);
      return fromHandoff;
    }
    return globalThis.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Synchronously reads the device's last-activated account ID. */
export function readLastActiveAccountId(): string | null {
  if (globalThis.localStorage === undefined) {
    return null;
  }
  try {
    return globalThis.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

/** Reactive accessor for this tab's pinned account ID. */
export function activeTabAccountId(): string | null {
  return tabAccountSignal();
}

export interface WriteTabAccountOptions {
  /**
   * When true, updates `sessionStorage` and `localStorage` WITHOUT firing the
   * DOM notification event or updating the Solid signal. Used during guest
   * merge/upgrade to prevent reactive listeners (such as PowerSync DB bindings)
   * from switching databases while a flush is in-flight. Call
   * {@link notifyTabAccountChanged} once the operation completes.
   */
  silent?: boolean;
}

/**
 * Pins this tab to the given account ID.
 * Also updates the device-wide default in `localStorage`.
 */
export function writeTabAccountId(userId: string, options: WriteTabAccountOptions = {}): void {
  try {
    globalThis.sessionStorage?.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
  } catch {
    // ignore
  }
  try {
    globalThis.localStorage?.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
  } catch {
    // ignore
  }
  if (!options.silent) {
    setTabAccountSignal(userId);
    notifyTabAccountChanged();
  }
}

/** Clears the tab pin. */
export function clearTabAccountId(): void {
  try {
    globalThis.sessionStorage?.removeItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    // ignore
  }
  setTabAccountSignal(null);
  notifyTabAccountChanged();
}

/** Emits the DOM event notifying subscribers that this tab's active account changed. */
export function notifyTabAccountChanged(): void {
  if (typeof globalThis.dispatchEvent === "function") {
    try {
      globalThis.dispatchEvent(new CustomEvent(TAB_ACCOUNT_CHANGED_EVENT));
    } catch {
      // ignore
    }
  }
}

/** Emits the DOM event notifying subscribers that the accounts registry changed. */
export function notifyAccountsChanged(): void {
  if (typeof globalThis.dispatchEvent === "function") {
    try {
      globalThis.dispatchEvent(new CustomEvent(ACCOUNTS_CHANGED_EVENT));
    } catch {
      // ignore
    }
  }
}
